import { useState } from 'react';
import type { TeamLiveData } from '../types';
import { TEAMS_BY_ABBR, teamFullName } from '../constants/teams';
import { TeamLogo } from './TeamLogo';
import { TeamScoutingCard } from './TeamScoutingCard';

interface Props {
  abbr: string;
  liveData?: TeamLiveData;
  ranking?: number;
  pastPrice?: number;
  onClose: () => void;
}

export function TeamDashboardModal({ abbr, liveData, ranking, pastPrice, onClose }: Props) {
  const meta = TEAMS_BY_ABBR[abbr];
  const [tab, setTab] = useState<'overview' | 'roster'>('overview');
  if (!meta) return null;

  const record = liveData?.record;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]" onClick={onClose}>
      <div
        className="panel w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with team color gradient */}
        <div
          className="p-5 flex items-center gap-4 relative"
          style={{ background: `linear-gradient(135deg, ${meta.color} 0%, ${meta.altColor ?? meta.color} 100%)` }}
        >
          <TeamLogo abbr={abbr} size={72} className="drop-shadow-lg" />
          <div className="flex-1 text-white">
            <div className="text-xs uppercase opacity-80">{meta.conference} {meta.division}</div>
            <div className="text-2xl font-extrabold drop-shadow">{teamFullName(abbr)}</div>
            <div className="text-sm opacity-90 flex gap-3 mt-1">
              {record && <span>📈 {record.wins}-{record.losses}{record.ties ? `-${record.ties}` : ''}</span>}
              {ranking && <span>🏆 #{ranking}</span>}
              {pastPrice != null && <span className="text-amber-200">💰 Last yr: ${pastPrice}</span>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-white/80 hover:text-white text-2xl"
          >×</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          {(['overview', 'roster'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2.5 text-sm font-semibold capitalize transition ${
                tab === t ? 'text-teal-400 border-b-2 border-teal-400' : 'text-slate-400 hover:text-white'
              }`}
            >{t}</button>
          ))}
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">
          {tab === 'overview' && (
            <div className="space-y-4">
              {/* Prior Year Sales & 2026 Scouting Dossier */}
              <TeamScoutingCard abbr={abbr} compact={false} />

              {!liveData && (
                <p className="text-slate-400 italic">Loading live team data from ESPN…</p>
              )}

              {/* Quick-stat tiles */}
              {liveData && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {liveData.fpi != null && (
                      <StatTile label="FPI" value={liveData.fpi.toFixed(1)} accent={liveData.fpi >= 0 ? 'text-emerald-300' : 'text-rose-300'} />
                    )}
                    {liveData.fpiRank != null && (
                      <StatTile label="FPI Rank" value={`#${liveData.fpiRank}`} accent="text-teal-300" />
                    )}
                    {liveData.projectedWins != null && (
                      <StatTile label="Proj Wins" value={liveData.projectedWins.toFixed(1)} accent="text-amber-300" />
                    )}
                    {liveData.sosRank != null && (
                      <StatTile
                        label="SOS Rank"
                        value={`#${liveData.sosRank}`}
                        accent={liveData.sosRank <= 10 ? 'text-rose-300' : liveData.sosRank >= 23 ? 'text-emerald-300' : 'text-slate-200'}
                        hint={liveData.sosRank <= 10 ? 'tough' : liveData.sosRank >= 23 ? 'easy' : 'avg'}
                      />
                    )}
                  </div>
                  {liveData.standing && (
                    <div className="card p-3">
                      <div className="text-xs text-slate-400 uppercase">Standing</div>
                      <div className="font-semibold text-white">{liveData.standing}</div>
                    </div>
                  )}
                  {liveData.nextGame && (
                    <div className="card p-3">
                      <div className="text-xs text-slate-400 uppercase">Next Game</div>
                      <div className="font-semibold text-white">
                        {liveData.nextGame.home ? 'vs' : '@'} {liveData.nextGame.opponent}
                        <span className="text-slate-400 ml-2 text-sm">
                          {liveData.nextGame.date && new Date(liveData.nextGame.date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                  <div>
                    <h4 className="text-sm font-bold text-slate-300 mb-2">⭐ Key Players</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {liveData.keyPlayers.length === 0 && (
                        <p className="text-slate-500 italic text-sm col-span-full">No key players found.</p>
                      )}
                      {liveData.keyPlayers.map(p => (
                        <div key={p.id} className="card p-2 flex items-center gap-2">
                          {p.headshot ? (
                            <img src={p.headshot} alt={p.name} className="w-10 h-10 rounded-full bg-slate-700" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-xs">
                              {p.position}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-white truncate">{p.name}</div>
                            <div className="text-[10px] text-slate-400">{p.position} #{p.jersey ?? '—'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {liveData && tab === 'roster' && (
            <div className="space-y-1 text-sm">
              {liveData.fullRoster.length === 0 && (
                <p className="text-slate-500 italic">Roster unavailable.</p>
              )}
              {liveData.fullRoster.map(p => (
                <div key={p.id} className="flex items-center justify-between py-1.5 border-b border-slate-800">
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-8 text-slate-500">{p.position}</span>
                    <span className="text-white">{p.name}</span>
                  </div>
                  <span className="text-xs text-slate-400">#{p.jersey ?? '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, accent, hint }: { label: string; value: string; accent: string; hint?: string }) {
  return (
    <div className="card p-2 text-center">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`text-base font-extrabold ${accent}`}>{value}</div>
      {hint && <div className="text-[9px] text-slate-500 uppercase">{hint}</div>}
    </div>
  );
}
